import { createContext, useContext, useEffect, useRef, useState } from "react";
import { API, authHeaders } from "@/lib/api";

/**
 * SINGLE source of truth for /api/system/activity + /api/events/stream.
 *
 * Why a context (not just a hook): before this, useSystemActivity was called
 * from Sidebar, LiveRibbon, Dashboard header, and Dashboard body — four
 * concurrent SSE readers + four polling intervals. On HTTP/1.1 the browser
 * caps concurrent connections per host at 6, so a PATCH triggered by a
 * feature-flag toggle could get queued behind the SSE readers and never
 * complete. All mutations must remain non-blocking; SSE is presentation.
 *
 * Reconnect strategy: exponential backoff (2s → 4s → 8s → 16s → cap 30s). The SSE
 * connection is decoupled from mutations. `streaming=false` never gates any
 * button.
 */
const Ctx = createContext({
  activeRuns: 0,
  events: [],
  sparkline: [],
  streaming: false,
  nextScheduled: null,
});

export function SystemActivityProvider({ children, pollMs = 3000 }) {
  const [state, setState] = useState({
    activeRuns: 0,
    events: [],
    sparkline: [],
    streaming: false,
    nextScheduled: null,
  });
  const attemptRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    let controller = new AbortController();

    const fetchOnce = async () => {
      try {
        const res = await fetch(`${API}/system/activity`, {
          headers: authHeaders(),
        });
        if (!res.ok || !alive) return;
        const d = await res.json();
        setState((s) => ({
          ...s,
          activeRuns: d.active_runs || 0,
          events: d.recent_events || [],
          sparkline: d.sparkline || [],
          nextScheduled: d.next_scheduled || null,
        }));
      } catch {
        /* ignore transient poll errors */
      }
    };

    const openStream = async () => {
      if (!alive) return;
      controller = new AbortController();
      try {
        const res = await fetch(`${API}/events/stream`, {
          headers: authHeaders(),
          signal: controller.signal,
        });
        if (!res.ok || !res.body) throw new Error(`sse http ${res.status}`);
        attemptRef.current = 0; // reset on success
        setState((s) => ({ ...s, streaming: true }));
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        while (alive) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          let sep;
          while ((sep = buf.indexOf("\n\n")) !== -1) {
            const raw = buf.slice(0, sep);
            buf = buf.slice(sep + 2);
            let evType = "message";
            const dataLines = [];
            for (const line of raw.split("\n")) {
              if (line.startsWith("event:")) evType = line.slice(6).trim();
              else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
            }
            if (!dataLines.length) continue;
            let payload;
            try {
              payload = JSON.parse(dataLines.join("\n"));
            } catch {
              continue;
            }
            if (evType === "activity") {
              setState((s) => ({
                ...s,
                events: [payload, ...s.events].slice(0, 8),
              }));
            }
          }
        }
      } catch {
        /* stream failed or aborted — will reconnect below */
      } finally {
        if (alive) setState((s) => ({ ...s, streaming: false }));
      }
      if (!alive) return;
      // Exponential backoff — 2s, 4s, 8s, 16s, capped at 30s
      const attempt = Math.min(attemptRef.current, 4);
      const delay = Math.min(2000 * Math.pow(2, attempt), 30000);
      attemptRef.current += 1;
      timerRef.current = setTimeout(openStream, delay);
    };

    fetchOnce();
    const t = setInterval(fetchOnce, pollMs);
    openStream();

    return () => {
      alive = false;
      clearInterval(t);
      if (timerRef.current) clearTimeout(timerRef.current);
      controller.abort();
    };
  }, [pollMs]);

  return <Ctx.Provider value={state}>{children}</Ctx.Provider>;
}

export function useSystemActivity() {
  return useContext(Ctx);
}
